import { InternationalizationAPI } from "../../context/translation.js";
import { DoubleColumnFormDesign, FormMetadata } from "../forms-types.js";
import { Descr } from "./VQF_902_1_customer.js";
import { TalerFormAttributes } from "@gnu-taler/taler-util";
import { vqfLanguagePolicy } from "./vqf.js";

export const form_vqf_902_5 = (
  i18n: InternationalizationAPI,
): FormMetadata => ({
  label: i18n.fixed({ en: "Customer Profile", de: "Kundenprofil" }),
  description: i18n.fixed({
    en: "The information below has to refer to the persons from whom the assets originate ultimately (e.g. beneficial owner of the assets, founder/creator of a trust or foundation). Is the customer an operational legal entity or partnership the information may refer to the entity itself (not to the controlling person), unless the entity holds the assets in trust for a third party.",
    de: "Die nachstehenden Angaben müssen sich auf die Personen beziehen, von denen die Vermögenswerte letztlich stammen (z. B. wirtschaftlich Berechtigter der Vermögenswerte, Gründer/Errichter eines Trusts oder einer Stiftung). Ist der Kunde eine operativ tätige juristische Person oder Personengesellschaft, können sich die Angaben auf die Gesellschaft selbst (nicht auf den Kontrollinhaber) beziehen, sofern die Gesellschaft die Vermögenswerte nicht treuhänderisch für einen Dritten hält.",
  }),
  id: "vqf_902_5",
  version: 1,
  config: VQF_902_5(i18n),
});

export function VQF_902_5(
  i18n: InternationalizationAPI,
): DoubleColumnFormDesign {
  return {
    type: "double-column",
    languagePolicy: vqfLanguagePolicy(i18n),
    sections: [
      {
        title: i18n.fixed({
          en: "Information on customer",
          de: "Angaben zum Kunden",
        }),
        description: Descr.CUSTOMER_INFO_TYPE(i18n),
        fields: [
          {
            id: TalerFormAttributes.CUSTOMER_NAME,
            label: i18n.fixed({ en: "Customer", de: "Kunde" }),
            type: "text",
            required: true,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Business activity",
          de: "Geschäftstätigkeit",
        }),
        fields: [
          {
            id: TalerFormAttributes.BIZREL_PROFESSION,
            label: i18n.fixed({
              en: "Profession, business activities, etc. (former, current, potentially planned)",
              de: "Beruf, Geschäftstätigkeiten usw. (frühere, aktuelle, allenfalls geplante)",
            }),
            type: "textArea",
            required: false,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Financial circumstances",
          de: "Finanzielle Verhältnisse",
        }),
        fields: [
          {
            id: TalerFormAttributes.BIZREL_INCOME,
            label: i18n.fixed({
              en: "Income and assets, liabilities (estimated)",
              de: "Einkommen und Vermögen, Verbindlichkeiten (geschätzt)",
            }),
            type: "textArea",
            required: false,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Origin of the deposited assets involved",
          de: "Herkunft der betroffenen eingebrachten Vermögenswerte",
        }),
        fields: [
          {
            id: TalerFormAttributes.BIZREL_HAVE_ASSETS,
            label: i18n.fixed({
              en: "Will the customer deposit assets with Taler Operations AG?",
              de: "Wird der Kunde Vermögenswerte bei der Taler Operations AG einbringen?",
            }),
            type: "choiceHorizontal",
            required: true,
            choices: [
              {
                label: i18n.fixed({ en: "Yes", de: "Ja" }),
                value: true,
              },
              {
                label: i18n.fixed({ en: "No", de: "Nein" }),
                value: false,
              },
            ],
          },
          {
            id: TalerFormAttributes.BIZREL_ORIGIN_NATURE,
            label: i18n.fixed({
              en: "Nature, amount and currency of deposited assets.",
              de: "Art, Betrag und Währung der eingebrachten Vermögenswerte.",
            }),
            type: "textArea",
            required: true,
            hide(value, root) {
              return !root[TalerFormAttributes.BIZREL_HAVE_ASSETS];
            },
          },
          {
            id: TalerFormAttributes.BIZREL_ORIGIN_CATEGORY,
            label: i18n.fixed({ en: "Category", de: "Kategorie" }),
            type: "choiceStacked",
            choices: [
              {
                label: i18n.fixed({ en: "Savings", de: "Ersparnisse" }),
                value: "SAVINGS",
              },
              {
                label: i18n.fixed({
                  en: "Own business operations",
                  de: "Eigene Geschäftstätigkeit",
                }),
                value: "OWN_BUSINESS",
              },
              {
                label: i18n.fixed({ en: "Inheritance", de: "Erbschaft" }),
                value: "INHERITANCE",
              },
              {
                label: i18n.fixed({ en: "Other", de: "Andere" }),
                value: "OTHER",
              },
            ],
            required: true,
            hide(value, root) {
              return !root[TalerFormAttributes.BIZREL_HAVE_ASSETS];
            },
          },
          {
            id: TalerFormAttributes.BIZREL_ORIGIN_CATEGORY_OTHER,
            type: "text",
            label: i18n.fixed({
              en: "Category clarification",
              de: "Erläuterung der Kategorie",
            }),
            required: true,
            hide(value, root) {
              return (
                root[TalerFormAttributes.BIZREL_ORIGIN_CATEGORY] !== "OTHER"
              );
            },
          },
          {
            id: TalerFormAttributes.BIZREL_ORIGIN_DETAIL,
            label: i18n.fixed({
              en: "Detailed description of the origins/economic background of the assets involved in the business relationship",
              de: "Detaillierte Beschreibung der Herkunft/des wirtschaftlichen Hintergrunds der in die Geschäftsbeziehung eingebrachten Vermögenswerte",
            }),
            type: "textArea",
            required: false,
            hide(value, root) {
              return (
                root[TalerFormAttributes.BIZREL_ORIGIN_CATEGORY] !== "OTHER"
              );
            },
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Nature and purpose of the business relationship",
          de: "Art und Zweck der Geschäftsbeziehung",
        }),
        fields: [
          {
            id: TalerFormAttributes.BIZREL_PURPOSE,
            label: i18n.fixed({
              en: "Purpose of the business relationship",
              de: "Zweck der Geschäftsbeziehung",
            }),
            type: "textArea",
            required: false,
          },
          {
            id: TalerFormAttributes.BIZREL_DEVELOPMENT,
            label: i18n.fixed({
              en: "Information on the planned development of the business relationship and the assets",
              de: "Angaben zur geplanten Entwicklung der Geschäftsbeziehung und der Vermögenswerte",
            }),
            type: "textArea",
            required: false,
          },
          {
            id: TalerFormAttributes.BIZREL_FINANCIAL_VOLUME,
            label: i18n.fixed({
              en: "Details on usual business volume",
              de: "Angaben zum üblichen Geschäftsvolumen",
            }),
            type: "textArea",
            required: false,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Relationship with third parties",
          de: "Beziehung zu Dritten",
        }),
        fields: [
          {
            id: TalerFormAttributes.BIZREL_THIRDPARTY_RELATIONSHIP,
            label: i18n.fixed({
              en: "Relation of the customer to the beneficial owner, controlling persons, authorised signatories and other persons involved in the business relationship",
              de: "Beziehung des Kunden zum wirtschaftlich Berechtigten, den Kontrollinhabern, den Zeichnungsberechtigten und anderen an der Geschäftsbeziehung beteiligten Personen",
            }),
            type: "textArea",
            required: false,
          },
          {
            id: TalerFormAttributes.BIZREL_THIRDPARTY_AMLA_FILES,
            label: i18n.fixed({
              en: "Relation to other AMLA-Files",
              de: "Bezug zu anderen GwG-Dossiers",
            }),
            type: "textArea",
            required: false,
          },
          {
            id: TalerFormAttributes.BIZREL_THIRDPARTY_REFERENCES,
            label: i18n.fixed({
              en: "Introducer / agents / references",
              de: "Vermittler / Beauftragte / Referenzen",
            }),
            type: "textArea",
            required: false,
          },
        ],
      },
      {
        title: i18n.fixed({ en: "Further information", de: "Weitere Angaben" }),
        fields: [
          {
            id: TalerFormAttributes.BIZREL_FURTHER_INFO,
            label: i18n.fixed({
              en: "Other relevant information",
              de: "Weitere relevante Angaben",
            }),
            type: "textArea",
            required: false,
          },
        ],
      },
    ],
  };
}
